import Avo from '@susu/custom-made/Avo';
import useScrollUp from '@susu/custom-made/hooks/useScrollUp';
import { Clients, Level } from '@susu/custom-made/utils/constants';
import { imageDefinition } from '@susu/custom-made/utils/imageDefinition';
import { imageUrlBuilder } from '@susu/custom-made/utils/imageUrlProvider/imageUrlProvider';
import type { MonogramItem, MonogramPositions, Option } from '@susu/custom-made/utils/interfaces';
import { changeLevel, getConfigurationTypeBySection } from '@susu/custom-made/utils/navigation';
import { MONOGRAM_TEST_IDS } from '@susu/custom-made/utils/testIds';
import { getImageUrl, onEnter } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import type { SyntheticEvent } from 'react';

import Button, { ButtonSize, ButtonType } from '../../button/button';
import Modal from '../../modal/modal';
import { useI18N } from '@susu/i18n/client';
import Panel, { PanelSize } from '../../panel/panel';
import { useEnvironmentContext } from '@susu/custom-made/providers/hooks/useEnvironmentContext';
import useFabricContext from '@susu/custom-made/providers/hooks/useFabricContext';
import useNavigationContext from '@susu/custom-made/providers/hooks/useNavigationContext';
import useMissingSelectionValuesContext from '@susu/custom-made/providers/hooks/useMissingSelectionValuesContext';
import useUserContext from '@susu/custom-made/providers/hooks/useUserContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';
import { useAvo } from '@susu/custom-made/hooks/useAvo';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';

const { ENABLE_AVO_TRACKING } = FEATURE_TOGGLES;

const onImageFailed = (e: SyntheticEvent<HTMLImageElement, Event>, fallbackImageUrl: string) => {
  if (fallbackImageUrl && e.currentTarget.src !== fallbackImageUrl) {
    e.currentTarget.src = fallbackImageUrl;
  }
};

const getMonogramSummary = (monogramData: Option, selected: MonogramItem, noneLabel: string) => {
  const colorName = monogramData.colors![selected.colorId ?? 0]?.name || noneLabel;
  const fontName = monogramData.fontTypes![selected.fontStyleId ?? 0]?.name || noneLabel;

  return `${selected.initials} • ${colorName} • ${fontName}`;
};

const MonogramPositionItem = ({
  position,
  monogramData,
  openMonogramDetails,
  editMonogram,
  deleteMonogram,
}: {
  position: MonogramPositions;
  monogramData: Option;
  openMonogramDetails: (positionId: number) => void;
  editMonogram: (position: MonogramPositions) => void;
  deleteMonogram: (position: MonogramPositions) => void;
}) => {
  const { t } = useI18N();
  const { selectedFabric } = useFabricContext();
  const { monogramsIdsWithMissingSelection } = useMissingSelectionValuesContext();
  const { selectedMonogram } = useOptionContext();
  const { cdnDomain } = useEnvironmentContext();

  const selected = selectedMonogram[position.id];
  const hasMissingValues = monogramsIdsWithMissingSelection?.includes(position.id);
  const imageUrls = getImageUrl(position.imageUrlTemplate, position.imageName, selectedFabric?.layerImageName || '');

  return (
    <div
      className={cn('flex justify-between items-center mb-10', {
        'cursor-pointer': !selected,
        'p-2 border-2 border-red-600 rounded-[8px]': hasMissingValues,
      })}
      onClick={
        !selected
          ? () => {
              openMonogramDetails(position.id);
            }
          : undefined
      }
      onKeyDown={
        !selected
          ? (e: React.KeyboardEvent<HTMLDivElement>) => {
              onEnter(e, () => {
                openMonogramDetails(position.id);
              });
            }
          : undefined
      }
      data-testid={MONOGRAM_TEST_IDS.getPositionId(position.id)}
    >
      <div className="w-[88px] h-[88px] shrink-0 md:w-[116px] md:h-[116px] 2xl:w-[140px] 2xl:h-[140px]">
        <img
          className="w-[88px] h-[88px] rounded-md md:w-[116px] md:h-[116px] 2xl:w-[140px] 2xl:h-[140px]"
          alt=""
          src={
            imageUrlBuilder({
              imageUrls: [imageUrls.imageUrl],
              imageDefinition: imageDefinition.thumbnail,
              noTransformation: true,
              cdnDomain,
            }).src
          }
          onError={e => {
            onImageFailed(
              e,
              imageUrlBuilder({
                imageUrls: [imageUrls.fallbackUrl],
                imageDefinition: imageDefinition.thumbnail,
                noTransformation: true,
                cdnDomain,
              }).src,
            );
          }}
        />
      </div>
      <div className="flex flex-[1_0_0] grow pr-8 pl-16 flex-col md:text-sm md:leading-sm md:px-20 2xl:text-base 2xl:leading-base 2xl:px-24 2xl:pl-16">
        <div
          className={cn(
            'text-[13px] font-medium leading-xs text-ellipsis overflow-hidden [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [line-clamp:2] md:text-sm md:leading-sm 2xl:text-base 2xl:leading-base',
            hasMissingValues ? 'text-red-600' : 'text-gray-800',
          )}
          data-testid={MONOGRAM_TEST_IDS.getPositionName(position.id)}
        >
          {position.name}
        </div>
        {selected && (
          <div
            className={cn(
              '[-webkit-box-orient:vertical] [-webkit-line-clamp:3] [line-clamp:3] text-ellipsis text-xs font-light leading-xs pt-2 2xl:text-sm 2xl:leading-sm',
              hasMissingValues ? 'text-red-600' : 'text-gray-800',
            )}
            data-testid={MONOGRAM_TEST_IDS.getPositionDescription(position.id)}
          >
            {getMonogramSummary(monogramData, selected, t('monogramPanel.none'))}
          </div>
        )}
      </div>
      {!selected && (
        <div className="shrink-0 mr-4 flex text-gray-600 py-[11.5px] px-12 [&>i]:text-xl 2xl:mr-20">
          <i className="susu-icons" translate="no">
            arrow_right
          </i>
        </div>
      )}
      {selected && (
        <div
          className={cn(
            'shrink-0 flex mr-4 justify-between [&>div]:cursor-pointer [&>div]:py-10 [&>div]:px-12 [&>div>i]:text-xl',
            hasMissingValues ? 'text-red-600' : 'text-gray-600',
          )}
        >
          <div
            onClick={() => {
              editMonogram(position);
            }}
            data-testid={MONOGRAM_TEST_IDS.getPositionEdit(position.id)}
          >
            <i className="susu-icons" translate="no">
              edit
            </i>
          </div>
          <div
            onClick={() => {
              deleteMonogram(position);
            }}
            data-testid={MONOGRAM_TEST_IDS.getPositionDelete(position.id)}
          >
            <i className="susu-icons" translate="no">
              trash
            </i>
          </div>
        </div>
      )}
    </div>
  );
};

export const MonogramDefaultPanel = ({
  monogramData,
  isMonogramShown,
  isDeleteMonogramModalOpen,
  deleteMonogramItem,
  editMonogram,
  deleteMonogram,
  closeDeleteMonogramModal,
  openMonogramDetails,
  confirmDeleteMonogram,
}: {
  monogramData: Option | undefined;
  isMonogramShown: boolean;
  isDeleteMonogramModalOpen: boolean;
  deleteMonogramItem: MonogramPositions | null;
  editMonogram: (positionI: MonogramPositions) => void;
  deleteMonogram: (position: MonogramPositions) => void;
  closeDeleteMonogramModal: () => void;
  openMonogramDetails: (positionId: number) => void;
  confirmDeleteMonogram: () => void;
}) => {
  const { t } = useI18N();
  const scrollUp = useScrollUp();
  const { section, setLevel } = useNavigationContext();
  const { client } = useUserContext();
  const { hiddenOptionsAndValues, selectedBaseStyle } = useOptionContext();
  const { getAvoStandardEventParams } = useAvo();
  const isAvoTrackingEnabled = useFeatureIsOn(ENABLE_AVO_TRACKING);
  const isInStore = client === Clients.inStore;
  const listContainerClasses = cn(
    'pt-[82px] px-16 pb-16 overflow-auto max-h-[calc(100dvh-76px-var(--webstore-header-height))] md:pt-[88px] md:px-20 md:pb-0 md:max-h-none md:h-[calc(100dvh-var(--webstore-header-height))] lg:pt-[82px] lg:pl-16 2xl:pt-[88px] 2xl:px-24',
    isInStore && 'pb-56',
  );

  const confirmMonogram = () => {
    changeLevel(Level.Group, setLevel);
    if (isAvoTrackingEnabled) {
      Avo.cmSelectionConfirmed({
        ...getAvoStandardEventParams(`${section}_options`),
        cmItemType: Avo.CmItemType.OPTION,
        currentBaseStyle: selectedBaseStyle[getConfigurationTypeBySection(section!)]?.name?.toLowerCase(),
        cmItemValue: 'monogram',
      });
    }
  };

  return (
    <>
      <Panel
        isActive={isMonogramShown}
        closeClick={confirmMonogram}
        panelSize={PanelSize.Content}
        testIdSubfix="monogram"
        zIndex={21}
      >
        {isMonogramShown && (
          <div>
            <div
              className="absolute text-lg font-medium leading-base tracking-[-0.3px] text-center text-gray-800 py-[43px] top-0 left-0 right-0 z-1 bg-white md:text-xl md:leading-lg md:tracking-normal md:pt-28 md:pb-32 lg:text-base lg:leading-base lg:pt-28 lg:pb-30 2xl:text-xl 2xl:leading-lg 2xl:pt-[26px] 2xl:pb-[34px]"
              data-testid={MONOGRAM_TEST_IDS.HEADER}
            >
              {t('monogramPanel.title')}
            </div>
            <div className="absolute top-[33px] left-[11px] z-1 md:top-[19px] md:left-[19px] lg:top-[17px] lg:left-[15px] 2xl:left-[19px]">
              <Button
                isFullSize={true}
                onClick={confirmMonogram}
                type={ButtonType.IconOnlyRound}
                size={ButtonSize.Large}
                testId={MONOGRAM_TEST_IDS.PANEL_CONFIRM_BTN}
              >
                <div className="flex items-center justify-center w-full">
                  <div className="text-gray-800 text-base flex md:text-xl">
                    <i className="susu-icons" translate="no">
                      arrow_left
                    </i>
                  </div>
                </div>
              </Button>
            </div>
            <div
              className={listContainerClasses}
              onScroll={e => {
                scrollUp.handleScroll(e, 82);
              }}
              data-testid={MONOGRAM_TEST_IDS.POSITION_LIST}
            >
              {monogramData?.positions &&
                Object.values(monogramData.positions)
                  .filter(
                    position =>
                      !hiddenOptionsAndValues ||
                      !hiddenOptionsAndValues.monogramPositions.includes(position.id),
                  )
                  .map(position => (
                    <MonogramPositionItem
                      key={'position_' + position.id}
                      position={position}
                      monogramData={monogramData}
                      openMonogramDetails={openMonogramDetails}
                      editMonogram={editMonogram}
                      deleteMonogram={deleteMonogram}
                    />
                  ))}
            </div>
          </div>
        )}
      </Panel>

      <Modal
        isOpen={isDeleteMonogramModalOpen}
        title={t('monogramPanel.removeConfirm', {
          position: monogramData?.positions && deleteMonogramItem
            ? String(monogramData?.positions[deleteMonogramItem.id]?.name ?? '')
            : '',
        })}
        firstButtonText={t('monogramPanel.remove')}
        firstButtonType={ButtonType.Primary}
        secondButtonText={t('monogramPanel.cancel')}
        secondButtonType={ButtonType.Tertiary}
        onFirstButtonClick={confirmDeleteMonogram}
        onSecondButtonClick={closeDeleteMonogramModal}
        onOverlayClick={closeDeleteMonogramModal}
        isFullSize={true}
        testId={MONOGRAM_TEST_IDS.REMOVE_MODAL}
      />
    </>
  );
};
